import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import type { UserTaskMirror } from "@/types/userTasks.ts";
import { userTaskQueries, useCheckTaskTool, useSetUserTaskMirror } from "./api.ts";

/**
 * What the last sync found, in a few words. okthx syncs every hour, before a
 * brief, and when the user asks, so the line says when it last did.
 */
function checkedLine(mirror: UserTaskMirror): string | null {
  if (!mirror.isDefault) return null;
  if (mirror.lastStatus === "failed") return `Couldn't reach ${mirror.label}`;
  if (mirror.lastStatus === "not-connected") return `${mirror.label} is not connected`;
  if (mirror.lastCheckedAtMs === null) return null;
  const time = new Date(mirror.lastCheckedAtMs).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `Synced ${time}`;
}

/**
 * One connected tool: the switch that mirrors the list to it, what the last
 * sync found, and Sync now while it is on.
 */
const MirrorRow = ({ mirror }: { mirror: UserTaskMirror }) => {
  const setMirror = useSetUserTaskMirror(mirror.label);
  const check = useCheckTaskTool(mirror.label);
  const { reset: resetSetMirror } = setMirror;
  const { reset: resetCheck } = check;
  // A newer sync answers a "busy" one: once the list shows it, the line says what it found.
  useEffect(() => {
    resetSetMirror();
    resetCheck();
  }, [resetSetMirror, resetCheck]);
  const busy = setMirror.data?.outcome?.status === "busy" || check.data?.status === "busy";
  const line = busy ? `Syncing with ${mirror.label} shortly` : checkedLine(mirror);
  const id = `tasks-mirror-${mirror.service}`;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-mirror={mirror.service}>
      <Label htmlFor={id} className="text-xs font-normal leading-normal text-muted-foreground">
        Sync with {mirror.label}
      </Label>
      <Toggle
        id={id}
        size="sm"
        checked={mirror.isDefault}
        disabled={setMirror.isPending}
        onCheckedChange={(isDefault) => setMirror.mutate({ service: mirror.service, isDefault })}
      />
      {line && <span className="text-muted-foreground">{line}</span>}
      {mirror.isDefault && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          data-statsig-name="tasks-sync-now"
          disabled={check.isPending}
          onClick={() => check.mutate({ service: mirror.service })}
        >
          Sync now
        </Button>
      )}
    </div>
  );
};

/**
 * The switch that makes a tool the one the user's new tasks go to. Shown only
 * for a tool the account has connected: connecting one does not start
 * syncing. Switching one tool on switches the others off; tasks already in a
 * tool stay there.
 */
export const MirrorControl = () => {
  const { data } = useQuery(userTaskQueries.mirror());
  const connected = (data?.mirrors ?? []).filter((mirror) => mirror.connected);
  if (connected.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {connected.map((mirror) => (
        <MirrorRow key={mirror.service} mirror={mirror} />
      ))}
    </div>
  );
};
